import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Play, ChevronDown, ChevronUp, Filter, Loader2 } from 'lucide-react';
import { benchmarkService } from '../../../services/benchmarkService';
import { promptRefinementService, type RunErrorAnalysis, type RefinementSession, type FieldAnalysis } from '../../../services/promptRefinementService';
import type { BenchmarkRun } from '../../../types/benchmark';
import { RunSelector } from '../RunSelector';
import { RunHistoryOverlay } from '../RunHistoryOverlay';
import { GenerateConfirmModal } from './GenerateConfirmModal';

const MAX_FIELDS = 10;

interface Props {
  session: RefinementSession | null;
  onComplete: (analysis: RunErrorAnalysis, session: RefinementSession) => void;
}

function getErrorBarColor(rate: number): string {
  if (rate < 0.15) return 'bg-green-500';
  if (rate < 0.40) return 'bg-orange-500';
  return 'bg-red-500';
}

function getErrorTypeBadgeColor(type: string): string {
  switch (type) {
    case 'missing': return 'bg-red-100 text-red-700';
    case 'hallucination': return 'bg-purple-100 text-purple-700';
    case 'format_incorrect': return 'bg-yellow-100 text-yellow-700';
    case 'field_confusion': return 'bg-blue-100 text-blue-700';
    case 'value_mismatch': return 'bg-orange-100 text-orange-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function formatErrorType(type: string): string {
  switch (type) {
    case 'missing': return 'Manquant';
    case 'hallucination': return 'Hallucination';
    case 'format_incorrect': return 'Format incorrect';
    case 'field_confusion': return 'Confusion champ';
    case 'value_mismatch': return 'Valeur incorrecte';
    default: return type;
  }
}

export function StepAnalyse({ session, onComplete }: Props) {
  const [runs, setRuns] = useState<BenchmarkRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>('');
  const [analysis, setAnalysis] = useState<RunErrorAnalysis | null>(null);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [showRunOverlay, setShowRunOverlay] = useState(false);

  // New: configurable threshold and field selection
  const [errorThreshold, setErrorThreshold] = useState(15);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Locked if session already has suggestions
  const isLocked = session != null && session.status !== 'pending';

  useEffect(() => {
    loadRuns();
  }, []);

  useEffect(() => {
    if (session?.error_analysis) {
      setAnalysis(session.error_analysis as unknown as RunErrorAnalysis);
      setSelectedRunId(session.benchmark_run_id);
    }
  }, [session]);

  // Auto-select fields: restore from session suggestions if available, otherwise use threshold
  useEffect(() => {
    if (!analysis) return;

    // If session already has suggestions, restore the fields that were included
    if (session?.suggestions) {
      const raw = session.suggestions as Record<string, unknown>;
      const fields = Array.isArray(raw.fields) ? raw.fields as Array<Record<string, unknown>> : [];
      if (fields.length > 0) {
        setSelectedFields(new Set(fields.map(f => f.field_name as string)));
        return;
      }
    }

    // Otherwise auto-select based on threshold
    const threshold = errorThreshold / 100;
    const aboveThreshold = analysis.fields
      .filter(f => f.error_rate > threshold)
      .sort((a, b) => b.error_rate - a.error_rate)
      .slice(0, MAX_FIELDS)
      .map(f => f.field_name);
    setSelectedFields(new Set(aboveThreshold));
  }, [analysis, errorThreshold, session]);

  const loadRuns = async () => {
    try {
      setLoadingRuns(true);
      const data = await benchmarkService.getRuns();
      setRuns(data.filter(r => r.status === 'completed'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors du chargement des runs');
    } finally {
      setLoadingRuns(false);
    }
  };

  const handleAnalyse = async () => {
    if (!selectedRunId) {
      toast.error('Veuillez sélectionner un run');
      return;
    }
    try {
      setLoadingAnalysis(true);
      const data = await promptRefinementService.getErrorAnalysisSummary(selectedRunId);
      setAnalysis(data);
      toast.success(`Analyse terminée — ${data.fields.length} champs évalués`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur lors de l'analyse");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleConfirmGenerate = async (params: { suggestion_targets: string[]; max_retries: number }) => {
    if (!selectedRunId || !analysis) return;
    setShowConfirmModal(false);
    try {
      setGenerating(true);
      const newSession = await promptRefinementService.generate({
        run_id: selectedRunId,
        selected_fields: Array.from(selectedFields),
        suggestion_targets: params.suggestion_targets,
        error_rate_threshold: errorThreshold / 100,
        max_retries: params.max_retries,
      });
      toast.success('Suggestions générées avec succès');
      onComplete(analysis, newSession);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const toggleField = (fieldName: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      next.has(fieldName) ? next.delete(fieldName) : next.add(fieldName);
      return next;
    });
  };

  const toggleFieldSelection = (fieldName: string) => {
    setSelectedFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else if (next.size < MAX_FIELDS) {
        next.add(fieldName);
      } else {
        toast.error(`Maximum ${MAX_FIELDS} champs sélectionnables`);
      }
      return next;
    });
  };

  const sortedFields: FieldAnalysis[] = useMemo(() => {
    if (!analysis) return [];
    return [...analysis.fields]
      .sort((a, b) => b.error_rate - a.error_rate)
      .filter(f => !showOnlyErrors || f.error_rate > 0);
  }, [analysis, showOnlyErrors]);

  const selectedRun = runs.find(r => r.id === selectedRunId);

  return (
    <div className="space-y-6">
      {/* Run selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Sélection du benchmark run</h2>

        <div className="flex items-end gap-4">
          <div className="flex-1">
            {loadingRuns ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 size={16} className="animate-spin" />
                Chargement des runs...
              </div>
            ) : (
              <RunSelector
                selectedRun={selectedRun}
                onClick={() => !isLocked && setShowRunOverlay(true)}
              />
            )}
          </div>

          {/* Threshold */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap">Seuil d'erreur :</label>
            <input
              type="number"
              value={errorThreshold}
              onChange={(e) => setErrorThreshold(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
              min={0}
              max={100}
              disabled={isLocked}
              className="w-16 border rounded px-2 py-2 text-sm text-center disabled:opacity-50"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>

          <button
            onClick={handleAnalyse}
            disabled={!selectedRunId || loadingAnalysis || isLocked}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingAnalysis ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            Analyser
          </button>
        </div>

        {isLocked && (
          <p className="text-xs text-gray-400 mt-2">
            L'analyse est verrouillée car des suggestions ont déjà été générées pour cette session.
          </p>
        )}
      </div>

      <RunHistoryOverlay
        isOpen={showRunOverlay}
        onClose={() => setShowRunOverlay(false)}
        runs={runs}
        selectedRunId={selectedRunId || null}
        onSelectRun={(runId) => { setSelectedRunId(runId); setAnalysis(null); }}
      />

      {/* Analysis results */}
      {analysis && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Résultats de l'analyse — {analysis.fields.length} champs
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {selectedFields.size}/{MAX_FIELDS} champs sélectionnés
              </span>
              <button
                onClick={() => setShowOnlyErrors(!showOnlyErrors)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  showOnlyErrors ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Filter size={14} />
                {showOnlyErrors ? 'Avec erreurs' : 'Tous'}
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{analysis.total_documents}</p>
              <p className="text-sm text-gray-500">Documents</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{analysis.total_fields_evaluated}</p>
              <p className="text-sm text-gray-500">Champs évalués</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{(analysis.overall_accuracy * 100).toFixed(1)}%</p>
              <p className="text-sm text-gray-500">Précision globale</p>
            </div>
          </div>

          {/* Field table with checkboxes */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-3 px-2 w-8"></th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Champ</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 w-64">Taux d'erreur</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Type dominant</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Erreurs</th>
                  <th className="py-3 px-4 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {sortedFields.map(field => {
                  const isSelected = selectedFields.has(field.field_name);
                  const isAboveThreshold = field.error_rate > errorThreshold / 100;
                  return (
                    <FieldRow
                      key={field.field_name}
                      field={field}
                      isExpanded={expandedFields.has(field.field_name)}
                      isSelected={isSelected}
                      isAboveThreshold={isAboveThreshold}
                      canSelect={isSelected || selectedFields.size < MAX_FIELDS}
                      onToggle={() => toggleField(field.field_name)}
                      onToggleSelect={() => !isLocked && toggleFieldSelection(field.field_name)}
                      disabled={isLocked}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Generate button */}
          <div className="mt-6 flex items-center justify-end">
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={generating || selectedFields.size === 0 || isLocked}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {generating ? <Loader2 size={18} className="animate-spin" /> : null}
              Générer les suggestions ({selectedFields.size} champs)
            </button>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {showConfirmModal && (
        <GenerateConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmGenerate}
          runId={selectedRunId}
          selectedFields={Array.from(selectedFields)}
          errorRateThreshold={errorThreshold / 100}
          defaultModelName={selectedRun?.model_name}
        />
      )}
    </div>
  );
}

function FieldRow({
  field,
  isExpanded,
  isSelected,
  isAboveThreshold,
  canSelect,
  onToggle,
  onToggleSelect,
  disabled = false,
}: {
  field: FieldAnalysis;
  isExpanded: boolean;
  isSelected: boolean;
  isAboveThreshold: boolean;
  canSelect: boolean;
  onToggle: () => void;
  onToggleSelect: () => void;
  disabled?: boolean;
}) {
  const errorPct = (field.error_rate * 100).toFixed(1);

  return (
    <>
      <tr className={`border-b border-gray-100 hover:bg-gray-50 ${!isAboveThreshold ? 'opacity-50' : ''}`}>
        <td className="py-3 px-2 text-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            disabled={disabled || (!canSelect && !isSelected)}
            className="rounded border-gray-300 text-blue-600"
          />
        </td>
        <td className="py-3 px-4 font-medium text-gray-900 cursor-pointer" onClick={onToggle}>
          {field.field_name}
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-gray-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full ${getErrorBarColor(field.error_rate)}`}
                style={{ width: `${Math.min(field.error_rate * 100, 100)}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-700 w-14 text-right">{errorPct}%</span>
          </div>
        </td>
        <td className="py-3 px-4">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getErrorTypeBadgeColor(field.dominant_error_type)}`}>
            {formatErrorType(field.dominant_error_type)}
          </span>
        </td>
        <td className="py-3 px-4 text-right font-medium text-gray-700">{field.error_count}</td>
        <td className="py-3 px-4 text-center cursor-pointer" onClick={onToggle}>
          {field.examples.length > 0 && (
            isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />
          )}
        </td>
      </tr>
      {isExpanded && field.examples.length > 0 && (
        <tr>
          <td colSpan={6} className="bg-gray-50 px-8 py-4">
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Exemples d'erreurs</p>
            <div className="space-y-2">
              {field.examples.map((ex, i) => (
                <div key={i} className="bg-white rounded border border-gray-200 p-3 text-sm">
                  <div className="flex items-center gap-4 mb-1">
                    <span className="font-medium text-gray-700">{ex.document_filename}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getErrorTypeBadgeColor(ex.error_type)}`}>
                      {formatErrorType(ex.error_type)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <span className="text-xs text-gray-500">Attendu :</span>
                      <p className="text-gray-900 font-mono text-xs mt-0.5">{ex.expected || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Extrait :</span>
                      <p className="text-gray-900 font-mono text-xs mt-0.5">{ex.extracted || '—'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
